import Prompt from '@components/Prompt'
import TokenAvatar from '@components/TokenAvatar'
import useLiquidium from '@plug-core/hooks/useLiquidium'
import { logger } from '@plug-core/logger'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { Button, Heading, Spinner, Surface, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Portfolio from './Portfolio'
import Positions from './Positions'
import TabSelector, { BorrowLendTab } from './TabSelector'
import { useLiquidiumBalances } from './useLiquidiumBalances'

const BorrowLend = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState<BorrowLendTab>('lending')
  const [creatingProfile, setCreatingProfile] = useState(false)
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const { profileId, markets, reserves, healthFactor, pending, createProfile } = useLiquidium()
  const balances = useLiquidiumBalances()

  const handleCreateProfile = async () => {
    setCreatingProfile(true)
    try {
      await createProfile()
    } catch (e) {
      logger.error('Failed to create Liquidium profile', { originalError: e })
      setPrompt({
        title: t('borrowLend.errors.title'),
        description: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setCreatingProfile(false)
    }
  }

  const renderMarkets = () => (
    <Surface padding="none" className="p-5">
      <Heading level={4} className="mb-3">
        {t('borrowLend.markets')}
      </Heading>

      {markets.map(({ pool, token, supplyRate, borrowRate }) => (
        <div key={pool.id} className="mb-3 flex items-center gap-3">
          <TokenAvatar logo={token?.logo} />

          <div className="min-w-0 flex-1">
            <Text weight="semibold" truncate>
              {token?.symbol ?? pool.asset}
            </Text>
          </div>

          <div className="flex flex-col items-end">
            <Text variant="body-s" tabular>
              APY {supplyRate.toFixed(2)}%
            </Text>
            <Text variant="body-s" tone="fg-2" tabular>
              APR {borrowRate.toFixed(2)}%
            </Text>
          </div>
        </div>
      ))}
    </Surface>
  )

  const renderContent = () => {
    if (profileId === undefined) {
      return (
        <div className="flex flex-col items-center py-8 text-fg-2">
          <Spinner size={40} />
        </div>
      )
    }

    if (profileId === null) {
      return (
        <div className="flex flex-col gap-4">
          <Surface className="flex flex-col items-center gap-3">
            <Heading level={4} align="center">
              {t('borrowLend.onboarding.title')}
            </Heading>
            <Text tone="fg-2" align="center">
              {t('borrowLend.onboarding.description')}
            </Text>
            <Button onClick={handleCreateProfile} variant="secondary" loading={creatingProfile}>
              {t('borrowLend.onboarding.createProfile')}
            </Button>
          </Surface>

          {!!markets.length && renderMarkets()}
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center">
          <Heading level="display" as="div" tabular>
            {formatMoney(Math.max(0, balances.supplied - balances.borrowed))}
          </Heading>

          <div className="mt-4 flex w-full gap-4">
            <Button
              variant="primary"
              fullWidth
              className="flex-1"
              onClick={() => navigate(ROUTES.FUND_BORROW_LEND('supply'))}
            >
              {t('borrowLend.supply')}
            </Button>

            <Button
              variant="secondary"
              fullWidth
              className="flex-1"
              onClick={() => navigate(ROUTES.FUND_BORROW_LEND('borrow'))}
            >
              {t('borrowLend.borrow')}
            </Button>
          </div>
        </div>

        <Portfolio balances={balances} healthFactor={healthFactor} newHealthFactor={undefined} />

        {!!pending.length && (
          <Surface className="flex items-center gap-3">
            <Spinner size={16} className="text-fg-2" />
            <Text variant="body-s" tone="fg-2">
              {t('borrowLend.pendingActivities', { count: pending.length })}
            </Text>
          </Surface>
        )}

        <TabSelector
          activeTab={activeTab}
          onTabChange={setActiveTab}
          labels={{
            lending: t('borrowLend.tabs.lending'),
            borrowing: t('borrowLend.tabs.borrowing'),
          }}
        />

        {activeTab === 'borrowing' &&
          (balances.supplied === 0 ? (
            <Surface className="flex flex-col items-center gap-3">
              <Heading level={4} align="center">
                {t('borrowLend.positions.supplyFirstTitle')}
              </Heading>
              <Text tone="fg-2" align="center">
                {t('borrowLend.positions.supplyFirstDescription')}
              </Text>
              <Button onClick={() => setActiveTab('lending')} variant="secondary">
                {t('borrowLend.positions.startLending')}
              </Button>
            </Surface>
          ) : (
            <Positions
              reserves={reserves}
              markets={markets}
              mode="borrow"
              onPositionClick={(asset) => navigate(ROUTES.FUND_BORROW_LEND('repay', asset))}
              onLeftButton={() => navigate(ROUTES.FUND_BORROW_LEND('borrow'))}
              onRightButton={() => navigate(ROUTES.FUND_BORROW_LEND('repay'))}
            />
          ))}

        {activeTab === 'lending' && (
          <Positions
            reserves={reserves}
            markets={markets}
            mode="supply"
            onPositionClick={(asset) => navigate(ROUTES.FUND_BORROW_LEND('withdraw', asset))}
            onLeftButton={() => navigate(ROUTES.FUND_BORROW_LEND('supply'))}
            onRightButton={() => navigate(ROUTES.FUND_BORROW_LEND('withdraw'))}
          />
        )}

        {!!markets.length && renderMarkets()}
      </div>
    )
  }

  return (
    <div className="mb-3 flex flex-col">
      {renderContent()}

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <div className="flex flex-col items-center">
          <div className="mb-1 flex flex-col items-center">
            <Heading level={4} align="center">
              {prompt?.title}
            </Heading>
            <Text tone="fg-2" align="center" className="mt-2 mb-4 whitespace-pre-line">
              {prompt?.description}
            </Text>
          </div>

          <Button onClick={() => setPrompt(null)} variant="secondary">
            {t('common.understood')}
          </Button>
        </div>
      </Prompt>
    </div>
  )
}

export default BorrowLend
